import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Copy } from "lucide-react";
import { toast } from "sonner";

export const DatePicker = () => {
  const [startDate, setStartDate] = useState<string>(
    new Date(2000, 0, 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [randomDate, setRandomDate] = useState<string | null>(null);

  const generateRandomDate = () => {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    
    if (start > end) {
      toast.error("Start date must be before end date");
      return;
    }

    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    
    const range = end - start;
    const randomTime = start + (array[0] / 0xFFFFFFFF) * range;
    const date = new Date(randomTime);
    
    const formatted = date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    setRandomDate(formatted);
    toast.success("Random date generated!");
  };

  const copyDate = () => {
    if (randomDate) {
      navigator.clipboard.writeText(randomDate);
      toast.success("Date copied to clipboard!");
    }
  };

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Calendar className="w-6 h-6 text-accent" />
        <h2 className="text-2xl font-bold text-foreground">Random Date</h2>
      </div>

      <div className="space-y-4 mb-4">
        <div>
          <Label htmlFor="start-date">Start Date</Label>
          <Input
            id="start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="end-date">End Date</Label>
          <Input
            id="end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1"
          />
        </div>
      </div>

      <Button onClick={generateRandomDate} className="w-full mb-4">
        Generate Random Date
      </Button>

      {randomDate && (
        <div className="bg-gradient-to-br from-accent to-accent/80 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <p className="text-lg font-semibold text-accent-foreground">
              {randomDate}
            </p>
            <Button
              size="sm"
              variant="ghost"
              onClick={copyDate}
              className="text-accent-foreground hover:bg-accent-foreground/10"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
